/**
 * Calendar-date helpers. Dates are ISO "YYYY-MM-DD" strings in UTC so that
 * phones, server and Domo agree on "days to follow-up" regardless of timezone.
 */

export type IsoDate = string;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function assertIsoDate(d: string): asserts d is IsoDate {
  if (!ISO_DATE.test(d) || Number.isNaN(Date.parse(d + 'T00:00:00Z'))) {
    throw new Error(`Invalid ISO date "${d}"`);
  }
}

function toUtc(d: IsoDate): Date {
  assertIsoDate(d);
  return new Date(d + 'T00:00:00Z');
}

function fromUtc(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

export function toIsoDate(d: Date): IsoDate {
  return fromUtc(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())));
}

export function addDays(d: IsoDate, days: number): IsoDate {
  const t = toUtc(d);
  t.setUTCDate(t.getUTCDate() + days);
  return fromUtc(t);
}

/** Adds calendar months, clamping to the last day of the target month (Jan 31 + 1 month = Feb 28/29). */
export function addMonths(d: IsoDate, months: number): IsoDate {
  const t = toUtc(d);
  const day = t.getUTCDate();
  t.setUTCDate(1);
  t.setUTCMonth(t.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(day, lastDay));
  return fromUtc(t);
}

/** Whole days from `a` to `b` (positive when b is later). */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86_400_000);
}

export function isWeekend(d: IsoDate): boolean {
  const day = toUtc(d).getUTCDay();
  return day === 0 || day === 6;
}

/** Adds working days (Mon–Fri). Public holidays are not modelled yet. */
export function addWorkingDays(d: IsoDate, days: number): IsoDate {
  let cur = d;
  let left = days;
  while (left > 0) {
    cur = addDays(cur, 1);
    if (!isWeekend(cur)) left--;
  }
  return cur;
}
